import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ThemeCartType, useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';

export const DEFAULT_THEME_CART: ThemeCartType = {
  cartType: 'drawer',
  labelCheckout: 'Checkout',
  labelContinueShopping: 'Continue shopping',
  showProductVendor: false,
  enableCartNote: false,
  drawerColorScheme: DEFAULT_THEME_COLOR_SCHEME,
  fixedCheckoutButton: false,
  pageCartColorScheme: DEFAULT_THEME_COLOR_SCHEME,
};

export default function ThemeCartSetting() {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const cart = builder.theme?.cart || DEFAULT_THEME_CART;

  const setValue = (value: Partial<ThemeCartType>) => {
    builder.setTheme({
      cart: { ...cart, ...value },
    });
  };

  return (
    <div>
      <div className='mb-4'>Cart type</div>
      <div className='mb-4'>
        <Select
          name='cartType'
          value={cart.cartType}
          onValueChange={(val) => setValue({ cartType: val })}
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent side='bottom' className='overflow-y-auto max-h-48'>
            <SelectItem value='drawer'>Drawer</SelectItem>
            <SelectItem value='page'>Page</SelectItem>
            <SelectItem value='popup_notification'>Popup notification</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {cart.cartType === 'page' && (
        <div className='flex mt-4 gap-x-1'>
          <Checkbox
            id='fixedCheckoutButton'
            aria-label='Select'
            className='mx-2 mt-1'
            checked={cart.fixedCheckoutButton}
            onCheckedChange={(checked) => {
              setValue({ fixedCheckoutButton: !!checked });
            }}
          />
          <div className='flex flex-col'>
            <label htmlFor='fixedCheckoutButton'>Fixed checkout button</label>
          </div>
        </div>
      )}
      <div className='flex mt-4 gap-x-1'>
        <Checkbox
          id='showProductVendor'
          aria-label='Select'
          className='mx-2 mt-1'
          checked={cart.showProductVendor}
          onCheckedChange={(checked) => {
            setValue({ showProductVendor: !!checked });
          }}
        />
        <div className='flex flex-col'>
          <label htmlFor='showProductVendor'>Show vendor</label>
        </div>
      </div>
      <div className='flex mt-4 gap-x-1'>
        <Checkbox
          id='enableCartNote'
          aria-label='Select'
          className='mx-2 mt-1'
          checked={cart.enableCartNote}
          onCheckedChange={(checked) => {
            setValue({ enableCartNote: !!checked });
          }}
        />
        <div className='flex flex-col'>
          <label htmlFor='enableCartNote'>Enable cart note</label>
        </div>
      </div>
      <div className='mt-4 mb-2 text-sm'>Label checkout</div>
      <Input
        name='labelCheckout'
        defaultValue={cart.labelCheckout}
        value={cart.labelCheckout}
        maxLength={30}
        onChange={(e) => {
          setValue({
            labelCheckout: e.target.value,
          });
        }}
      />
      <div className='mt-4 mb-2 text-sm'>Label continue shopping</div>
      <Input
        name='labelContinueShopping'
        defaultValue={cart.labelContinueShopping}
        value={cart.labelContinueShopping}
        maxLength={30}
        onChange={(e) => {
          setValue({
            labelContinueShopping: e.target.value,
          });
        }}
      />
      <div className='mt-4 mb-2 text-sm'>Color scheme</div>
      {cart.cartType === 'page' ? (
        <SchemeSetting
          value={cart.pageCartColorScheme}
          onChange={(theme) => {
            setValue({
              pageCartColorScheme: theme,
            });
          }}
        />
      ) : (
        <SchemeSetting
          value={cart.drawerColorScheme}
          onChange={(theme) => {
            setValue({
              drawerColorScheme: theme,
            });
          }}
        />
      )}
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>
    </div>
  );
}
